import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { DGPTAlertEventInstance, DGPTAlertRun } from '../../types/dgptAlerts';
import { useAlertConfigById } from './hooks/useAlertConfigs';
import {
  useAlertEventInstances,
  useUpdateAlertEventInstance,
} from './hooks/useAlertEventInstances';
import PageTitle from '../headers/PageTitle';
import { StylishNewButton } from '../../components/DesignSystems/New/StylishNewButton';
import { Alert, Modal, Badge } from 'react-bootstrap';
import { Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import LegacyPratusTable from '../../components/DesignSystems/PratusTable/LegacyPratusTable';
import { useAlertAcknowledgements } from './hooks/useAlertAcknowledgements';

interface TableColumn {
  dataField: string;
  text: string;
  sort: boolean;
  isDummyField?: boolean;
  formatter?: (cell: unknown, row?: DGPTAlertEventInstance) => React.ReactNode;
}

// Component to check and display acknowledgment status for each instance
const AckRequiredCell: React.FC<{ instance: DGPTAlertEventInstance }> = ({
  instance,
}) => {
  const { data: acknowledgements = [] } = useAlertAcknowledgements(instance.id);
  const hasAcknowledgements = acknowledgements.length > 0;

  return (
    <Badge bg={hasAcknowledgements ? 'warning' : 'secondary'}>
      {hasAcknowledgements ? 'Yes' : 'No'}
    </Badge>
  );
};

const AlertInstanceTable: React.FC = () => {
  const navigate = useNavigate();
  const { alert_config_id: alertConfigId } = useParams<{
    alert_config_id: string;
  }>();
  const { data: config, isLoading: isLoadingConfig } = useAlertConfigById(
    alertConfigId || ''
  );
  const { data: instances, isLoading: isLoadingInstances } =
    useAlertEventInstances(alertConfigId || '');
  const updateMutation = useUpdateAlertEventInstance();
  const [selectedInstance, setSelectedInstance] =
    useState<DGPTAlertEventInstance | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showInactive, setShowInactive] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const perPage = 10;

  const handleCloseModal = (): void => {
    setShowDetailsModal(false);
    setSelectedInstance(null);
  };

  const handleToggleArchive = async (
    instance: DGPTAlertEventInstance
  ): Promise<void> => {
    try {
      const updates: Partial<DGPTAlertEventInstance> = {
        archived: !instance.archived,
      };
      await updateMutation.mutateAsync({
        id: instance.id,
        configId: alertConfigId,
        updates,
      });
    } catch (error) {
      console.error('Error toggling archive status:', error);
    }
  };

  // Filter instances based on showInactive toggle
  const filteredInstances =
    instances?.filter((instance: DGPTAlertEventInstance) => {
      return showInactive ? !instance.archived : instance.archived;
    }) || [];

  // Combine loading states
  const isLoading = isLoadingConfig || isLoadingInstances;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!config) {
    return <div>Alert configuration not found</div>;
  }

  const instanceCount = filteredInstances?.length || 0;
  const hasInstances = instanceCount > 0;

  const columns: TableColumn[] = [
    {
      dataField: 'name',
      text: 'Name',
      sort: true,
    },
    {
      dataField: 'createdAt',
      text: 'Created At',
      sort: true,
      formatter: (cell: unknown) => new Date(cell as string).toLocaleString(),
    },
    {
      dataField: 'archived',
      text: 'Status',
      sort: true,
      formatter: (cell: unknown) => ((cell as boolean) ? 'Inactive' : 'Active'),
    },
    {
      dataField: 'acknowledgmentRequired',
      text: 'Acknowledgment Required',
      sort: false,
      formatter: (cell: unknown, row?: DGPTAlertEventInstance) => {
        if (!row) return null;
        return <AckRequiredCell instance={row} />;
      },
    },
    {
      dataField: 'dgptSitrepId',
      text: 'SITREP',
      sort: true,
      formatter: (cell: unknown) => {
        if (!cell) return 'No SITREP';
        return <Link to={`/sitreps/${cell}`}>View SITREP</Link>;
      },
    },
    {
      dataField: 'recipients',
      text: 'Recipients',
      sort: false,
      isDummyField: true,
      formatter: (cell: unknown, row?: DGPTAlertEventInstance) => {
        if (!row) return null;
        return (
          <button
            className="btn btn-link p-0"
            onClick={() => {
              navigate(
                `/aialerts/${alertConfigId}/reports/${row.id}/recipients`
              );
            }}
          >
            View Recipients
          </button>
        );
      },
    },
    {
      dataField: 'actions',
      text: 'Actions',
      sort: false,
      isDummyField: true,
      formatter: (cell: unknown, row?: DGPTAlertEventInstance) => {
        if (!row) return null;
        return (
          <button
            className="btn btn-link p-0"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleArchive(row);
            }}
            disabled={updateMutation.isPending}
          >
            {row.archived ? 'Activate' : 'Deactivate'}
          </button>
        );
      },
    },
  ];

  return (
    <>
      <div className="p-4 pt-0">
        <PageTitle
          title={`Alert Reports - ${config.name}`}
          parents={['Alert Configurations', config?.name || 'Loading...']}
          onBack={() => navigate('/aialerts')}
        />
        <p className="text-muted mb-4">
          When a trigger condition is met, a Report is created and generates a
          SITREP. DisasterGPT monitors for changes in the situation - if
          conditions remain the same, no new alert is sent. A new alert is only
          generated when the trigger severity changes. You can view the alert
          checks in the{' '}
          <Link to={`/aialerts/${alertConfigId}/logs`}>Logs table</Link>.
        </p>
        <div className="bg-neutral-800 rounded-1 p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-2">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showInactiveToggle"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="showInactiveToggle"
                >
                  {showInactive ? 'Active' : 'Inactive'}
                </label>
              </div>
            </div>
            <div className="text-muted small">
              {showInactive
                ? `${filteredInstances.length} active reports`
                : `${filteredInstances.length} inactive reports`}
            </div>
          </div>

          {!hasInstances && (
            <Alert variant="info" className="mt-3">
              <strong>No Alert Reports Found</strong>
              <p className="mb-0">
                {showInactive
                  ? 'There are no active alert reports to display.'
                  : 'There are no inactive alert reports to display.'}
              </p>
            </Alert>
          )}

          {hasInstances && (
            <LegacyPratusTable
              keyField="id"
              columns={columns}
              rows={filteredInstances}
              pagination
              page={page}
              perPage={perPage}
              total={filteredInstances.length}
              onPageChange={setPage}
              noDataIndication="No alert reports found"
            />
          )}
        </div>
      </div>

      <Modal show={showDetailsModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Alert Report Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInstance && (
            <div>
              <h6>Report Information</h6>
              <p>
                <strong>Name:</strong> {selectedInstance.name}
              </p>
              <p>
                <strong>Status:</strong>{' '}
                {selectedInstance.archived ? 'Inactive' : 'Active'}
              </p>
              <p>
                <strong>Created:</strong>{' '}
                {format(new Date(selectedInstance.createdAt), 'PPpp')}
              </p>
              <p>
                <strong>Updated:</strong>{' '}
                {format(new Date(selectedInstance.updatedAt), 'PPpp')}
              </p>

              <h6 className="mt-3">Associated Runs</h6>
              {selectedInstance.alertRuns &&
              selectedInstance.alertRuns.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Run ID</th>
                        <th>Status</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInstance.alertRuns.map(
                        (run: DGPTAlertRun, index: number) => (
                          <tr key={index}>
                            <td>{run.id}</td>
                            <td>{run.status}</td>
                            <td>{format(new Date(run.createdAt), 'PPpp')}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>No associated runs available</p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <StylishNewButton
            className="button--secondary"
            onClick={handleCloseModal}
          >
            Close
          </StylishNewButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AlertInstanceTable;
